/**
 * "สคริปต์แนะนำรายคน" (owner 17 ต.ค. 69): what the API worked out for one conversation from this chat, POS, the
 * carriers and the website — which stage a new customer is at, what we already know / still have to ask, what to
 * answer next (first = recommended), and for a returning customer their buying rhythm, an upsell and the problems
 * they had. The system never sends anything: the staff move a suggestion into the typing box and press send.
 *
 *   GET  /api/v1/conversations/:id/script                → ConversationScript (404 = none)
 *   POST /api/v1/conversations/:id/script/refresh        → ConversationScript (compute now)
 *   GET  /api/v1/conversations/:id/script/repeat-order   → { cart } (404 = nothing to repeat)
 *   POST / DELETE /api/v1/conversations/:id/script/steps/:key/done → ConversationScript (ขั้นตอนการขาย: a custom step
 *        ticked / unticked by staff)
 *   POST /api/v1/conversations/:id/order-read {auto?} → OrderRead ("เติมจากแชททั้งหมด": the AI reads the whole chat for
 *        the order tab — api/app/ai/order_read.py)
 *   WS   { type: "script.updated", conversation_id, style_key? } → reload it if that chat is open (+ the row's style)
 *   Owner switch `ai_settings.script_live` (ตั้งค่า › AI): compute on every customer message (default on).
 *
 * The API may send more fields than these; the UI ignores them. Missing lists are read as empty
 * (lib/script.ts normalizeScript).
 */

export type ScriptSegment = 'new' | 'returning'

/** The 6 steps of a new customer (docs: How.dc.html — ทักทาย · ค่าส่ง+วันได้ของ · แพ็ก · เมนู · ใบสรุปยอด · ชำระ+ทักตาม). */
export type ScriptStageKey = 'greet' | 'fee_date' | 'pack' | 'menu' | 'summary' | 'pay'

/** Where a fact came from. */
export type ScriptFactSource = 'chat' | 'pos' | 'web'

/** answer = the customer's open question · opener = shows we remember them · apology = a past problem (never first) ·
 *  upsell = a bigger pack · ask = the next thing to find out · followup = nudge after silence. */
export type SuggestionKind = 'answer' | 'opener' | 'apology' | 'upsell' | 'ask' | 'followup'

/** One piece of a suggestion: plain words, a value the system filled in (highlighted), or a blank the staff fill. */
export type ScriptPart =
  | { t: 'text'; text: string }
  | { t: 'fact'; text: string; source?: string | null }
  | { t: 'gap'; label: string }

export interface ScriptStage {
  n: number
  of: number
  key: ScriptStageKey
  label: string
}

/** A customer message nobody answered yet. */
export interface ScriptPending {
  message_id: string
  text: string
  at: string
  topic: string | null
  topic_label: string | null
  /** คู่มือปิดการขาย: the reply type read in this message (the playbook's own message only). */
  pb_type?: string | null
  pb_label?: string | null
}

/**
 * คู่มือปิดการขาย (owner 18 ต.ค. 69, api/app/ai/script/playbook.py): how the customer replied to the step now —
 * options (ถามตัวเลือก) · price_objection (ติดราคา) · hesitate (ลังเล) · trial (อยากลองก่อน) · delivery (ถามเรื่องส่ง) ·
 * menu_q (ถามเมนู) · quality (ถามคุณภาพ) · agree (ตกลง) · other (nothing in the playbook reads it: the step card + the
 * AI button in the draft box).
 */
export interface ScriptPlaybook {
  type: string
  label: string
  message_id: string | number
  /** The customer's words ("มีแพคอื่นอีกไหม"). */
  text: string
  /** It asks something (else an objection / a statement). */
  question: boolean
  /** The library answer type used (pb_options …, or the owner's topic: ship_area · menu) — null for "other". */
  answer_type: string | null
  /** ลังเล: mark the chat for ทักตาม. */
  follow_up: boolean
  /** The step that was current when it was read. */
  step: string | null
}

/** The panel's note on the step now: the customer replied to it with a question / an objection. */
export interface ScriptCustomerReply {
  text: string
  type: string
  label: string
  question: boolean
  /** false = nothing in the playbook reads it ("ให้ AI พาไปขั้นต่อไป"). */
  in_playbook: boolean
}

export interface ScriptFact {
  key: string
  label: string
  value: string
  source: ScriptFactSource
}

/**
 * ขั้นตอนการขาย (owner 17 ต.ค. 69, ตั้งค่า › ขั้นตอนการขาย): one step of the panel's checklist — what to ask the customer
 * next, in the owner's order, then the system steps สรุปยอด → ชำระ.
 *   done     the chat / POS / website already answers it (auto), staff ticked it (custom), or the system saw it
 *   current  the first step not done (its question goes into the typing box with "ใส่ในช่องพิมพ์")
 *   todo     a later step not done yet
 *   system   สรุปยอด / ชำระ before every question step is done (greyed)
 */
export type ScriptStepStatus = 'done' | 'current' | 'todo' | 'system'

export interface ScriptStep {
  /** area · meals · pack · avoid · menu · start · address · custom:<n> · summary · pay */
  key: string
  label: string
  /** The words to ask (the owner's, or the more precise ask for a place given in part). null for a system step. */
  question: string | null
  status: ScriptStepStatus
  /** auto = ticked by the system from the chat · custom = staff tick it · system = สรุปยอด / ชำระ */
  kind?: 'auto' | 'custom' | 'system'
  /** What the customer said / what we know ("เขตบางนา", "วันละ 2 มื้อ"). */
  value?: string | null
  /** The shop already asked it and the customer has not answered yet. */
  asked?: boolean
  /** answered but not enough yet (a Bangkok postcode alone): why the step is still current */
  note?: string | null
  /** The pack step of a new customer not chosen yet: the main pack the system recommends from what the customer said
   *  (meals a day × days, owner 18 ต.ค. 69) — null when the customer said neither. */
  suggest?: ScriptPackSuggest | null
  /** ทดลอง A/B (owner 18 ต.ค. 69): the pack / menu step (not done) of a chat whose list asks the customer's goal first,
   *  once the goal is known — a short hint for the staff + the dishes of this round that fit it (approved numbers only). */
  goal_hint?: ScriptGoalHint | null
  /** คู่มือปิดการขาย: the step now, the customer replied with a question / an objection — shown instead of
   *  "ถามไปแล้ว รอลูกค้าตอบ". */
  customer_reply?: ScriptCustomerReply | null
}

/** The customer's goal on the pack / menu step (api/app/ai/script engine, ทดลอง A/B variant A). */
export interface ScriptGoalHint {
  /** protein · low_kcal · low_sugar · convenience · balanced · family · jae */
  goal: string
  /** "โปรตีนสูง" */
  label: string
  /** "ลูกค้าเน้นโปรตีนสูง: ชวนเลือกเมนูที่โปรตีนสูง" — for the staff, never sent */
  text: string
  /** "เมนูรอบนี้ที่โปรตีนสูงที่สุด" — null when no list */
  dishes_title: string | null
  /** ≤ 3, the numbers exactly as the approved "เมนูรอบนี้" has them · [] = the round has no numbers for it */
  dishes: { menu_id: number; name: string; value: string }[]
}

/** ทดลอง A/B: a new-customer chat in the test (the variant never changes for that chat). */
export interface ScriptExperiment {
  key: string
  variant: 'A' | 'B'
}

/** A main pack of the POS catalog recommended for a new customer (api/app/ai/script/engine.py pack_suggestion). */
export interface ScriptPackSuggest {
  set_menu_id: number
  /** "แพ็ก 7 วัน 21 มื้อ" — the name the customer reads */
  name: string
  /** the POS name (the order tab's pack list) */
  pos_name?: string | null
  price: number | null
  meals: number
  days: number
  /** price ÷ meals, whole baht */
  per_meal: number | null
  /** meals ÷ the customer's meals a day (rounded down) — null when only the days are known */
  lasts_days: number | null
  /** "วันละ 2 มื้อ · 15 วัน" — what the recommendation is from */
  basis: string | null
}

export interface ScriptMissing {
  key: string
  label: string
  hint: string | null
}

/** One reason behind a suggestion ("(i)"): a number from the shop's chat statistics, the chat, POS … */
export interface ScriptWhy {
  text: string
  /** chat · pos · web · stats · shipping … (free text; lib/script.ts names the known ones in Thai). */
  source: string | null
}

export interface ScriptSuggestion {
  id: string
  kind: SuggestionKind
  title: string
  /** The whole message as one string (gaps written as "[label]"). */
  text: string
  parts: ScriptPart[]
  why: ScriptWhy[]
  /** คู่มือปิดการขาย: the customer reply type this answer was written for (counted with the send). */
  pb_type?: string | null
}

export interface ScriptPackRef {
  name: string
  meals: number | null
  per_meal?: number | null
}

export interface ScriptUpsell {
  reason: 'ran_out_fast' | 'same_pack_repeated'
  /** One Thai line, e.g. "3 รอบล่าสุดสั่งแพ็ก 20 มื้อ ทุก ~14 วัน". */
  detail: string
  from: ScriptPackRef
  to: ScriptPackRef
  /** The suggestion that invites the customer (in `suggestions`). */
  suggestion_id: string | null
}

export interface ScriptReturning {
  orders: number
  total: number | null
  last_at: string | null
  usual_gap_days: number | null
  /** Days past their usual gap (> 0 = later than usual). */
  overdue_days: number | null
  last_pack: { name: string; meals: number | null } | null
  upsell: ScriptUpsell | null
  /** Meals still left on an open pack. */
  open_meals: number | null
  repeat_last_order: { available: boolean; summary: string } | null
}

export type ScriptProblemKind = 'delivery_late' | 'delivery_failed' | 'payment_failed' | 'web_error' | 'claim'

export interface ScriptProblem {
  kind: ScriptProblemKind | string
  text: string
  at: string | null
}

/** An answer type the chat needed that has no active answer in the library (nothing is written for it). */
export interface ScriptUndefinedTopic {
  key: string
  label: string
}

/**
 * แนวลูกค้า (owner 17 ต.ค. 69, api/app/ai/script/style.py, docs/script.md "แนวลูกค้า"): how the customer writes, read by
 * rules from their last 5 conversational messages (addresses, pasted menus/carts, links are skipped; stickers count as
 * casual) — fewer than 2 = polite_short. Never changes what the shop says; the panel shows it, "ปรับตามแนวลูกค้า"
 * gives it to the AI, and a curt customer waiting for us gets "ตอบภายใน 5 นาที" (lib/style.ts).
 */
export type CustomerStyleKey = 'polite_short' | 'polite_detailed' | 'casual' | 'curt' | 'english'

/** What the customer said they care about (only when they said it), in the report's order:
 *  delivery_date · price · weight · storage · trial · fresh · taste · avoid · for_others · busy · condition · rice. */
export interface CustomerMotive {
  key: string
  label: string
}

export interface CustomerStyle {
  key: CustomerStyleKey
  /** สุภาพ กระชับ · สุภาพ ละเอียด · เป็นกันเอง · ห้วน ตอบสั้น · ภาษาอังกฤษ */
  label: string
  /** = segment "returning". */
  returning: boolean
  motives: CustomerMotive[]
  /** How many of the customer's messages it was read from (0–5). */
  basis: number
}

export interface ConversationScript {
  conversation_id: string
  segment: ScriptSegment
  computed_at: string
  /** Computed before the newest customer message. */
  stale: boolean
  /** The owner's "คิดทุกข้อความลูกค้า" switch (`script_live`). */
  enabled: boolean
  /** New customers only. */
  stage: ScriptStage | null
  pending: ScriptPending[]
  known: ScriptFact[]
  missing: ScriptMissing[]
  /** ขั้นตอนการขาย of the customer's segment (an older API: absent = empty). */
  steps: ScriptStep[]
  /** First = recommended. An apology is never first (the UI makes sure too). */
  suggestions: ScriptSuggestion[]
  returning: ScriptReturning | null
  problems: ScriptProblem[]
  /**
   * Owner 17 ต.ค. 69 "ต้อง fix เลย … กำหนดโดยฉันเอง": every suggestion is the owner's own answer from the library; the
   * types this chat needed without an active answer are listed here ("ยังไม่ได้กำหนดคำตอบเรื่อง …"). Absent = none.
   */
  undefined_topics: ScriptUndefinedTopic[]
  /** แนวลูกค้า — null for a chat that is not a direct chat, or a script computed before it existed. */
  style?: CustomerStyle | null
  /**
   * สรุปยอด → "เปิดลงออเดอร์ (เติมให้แล้ว)" (owner 18 ต.ค. 69): what the chat collected for the order tab, in the
   * "สั่งชุดเดิมซ้ำ" cart shape + the start day, the place for a new POS customer and the foods to avoid. null = nothing
   * yet (or a script computed before it existed). Never saved by itself: staff pick the dishes, check and save.
   */
  order_prefill?: OrderPrefill | null
  /** ทดลอง A/B (owner 18 ต.ค. 69): set for a new-customer chat in the test — the panel shows "ทดลองแบบ A/B" (never which
   *  variant); null / absent = not in it. */
  experiment?: ScriptExperiment | null
  /** คู่มือปิดการขาย (owner 18 ต.ค. 69): how the customer replied to the step now — null / absent = nothing to read. */
  playbook?: ScriptPlaybook | null
  /**
   * การสั่งครั้งนี้ (owner 18 ต.ค. 69 "ระวังเวลาลูกค้าเก่าสั่งซื้อใหม่ ไม่ใช่ไปอ่านออเดอร์เก่ามานะ"): the checklist and
   * `order_prefill` read only the chat after this boundary — null / absent = the whole chat (a first-time customer).
   */
  session?: ScriptSession | null
}

/**
 * การสั่งครั้งนี้ (api/app/ai/script/session.py): the purchase being talked about now starts after the latest of the
 * chat's last saved order (done / held), the customer's newest POS order and a 3-day silence.
 */
export interface ScriptSession {
  /** Messages after this time are this purchase's (ISO). */
  since: string
  /** The first message of this purchase (null = nothing said since). */
  start: string | null
  reason: 'order' | 'pos' | 'silence' | string
  /** The earlier order's time · the last message before the silence. */
  at: string | null
  quiet_days: number | null
  /** "อ่านเฉพาะแชทตั้งแต่ 15 ต.ค. 14.20 น. (ออเดอร์ก่อนหน้า 12 ต.ค. 10.05 น.)" */
  label: string | null
}

/**
 * "สั่งชุดเดิมซ้ำ": what goes into the order tab. The brief says "the existing AI-cart / order-tab prefill shape"; the
 * order tab is filled from a pack + menus (stores/order.ts applyMenuRead / repeat), so every field is optional and the
 * UI reads either spelling: `set_menu_id` / `setMenuId`, or a pack name (`set_menu_name` / `package`) matched to the
 * POS catalog; `items` = menus × qty (`menu_id`); `shipping`; a label for the "สั่งซ้ำจาก …" line.
 */
export interface RepeatOrderCart {
  /** The hour the customer said they want it delivered (7–21) and their words — the order tab picks the slot it falls in. */
  time_hour?: number | null
  /** A POS slot key ("14:00") the chat settled on ("เติมจากแชททั้งหมด") — used only while the POS still has that slot. */
  time?: string | null
  time_label?: string | null
  time_wanted?: string | null
  set_menu_id?: number | null
  setMenuId?: number | null
  set_menu_name?: string | null
  package?: string | null
  items?: { menu_id: number; qty: number }[] | null
  shipping?: 'lalamove' | 'dhl' | string | null
  carrier?: string | null
  order_code?: string | null
  /** "จากออเดอร์ #24318" (AiCart.source), or the API's OrderForm.source {kind: "repeat", label: "สั่งซ้ำจากออเดอร์ …"}. */
  source?: string | { kind?: string | null; label?: string | null } | null
  label?: string | null
  title?: string | null
  total?: number | null
  // ---- the summary handoff (order_prefill) adds these; "สั่งชุดเดิมซ้ำ" leaves them out
  /** "แพ็ก 7 วัน 21 มื้อ" */
  pack_label?: string | null
  /** The pack is the system's recommendation, the customer has not chosen it. */
  pack_suggested?: boolean | null
  /** YYYY-MM-DD the POS delivery date — only a day the calendar can deliver (null = the requested one cannot be). */
  date?: string | null
  /** "จ. 19 ต.ค." — the day the customer asked for */
  date_label?: string | null
  date_wanted?: string | null
  /** Why the requested day is not preselected. */
  date_problem?: string | null
  /** The chat cut-off of the day before: after it the day is gone (checked again when pressed). */
  date_open_until?: string | null
  /** A new POS customer's address from the chat (the order tab's "เพิ่มลูกค้าใหม่ใน POS" form). */
  address?: PrefillAddress | null
  /** หมายเหตุถึงครัว: the foods to avoid. */
  remark?: string | null
  /** Lines for the staff above the form ("แพ็กที่ระบบแนะนำ …", "วันที่ลูกค้าขอ: …"). */
  notes?: string[] | null
  /** การสั่งครั้งนี้: "อ่านเฉพาะแชทตั้งแต่ … (ออเดอร์ก่อนหน้า …)" — null = the whole chat was read. Shown first in the notes. */
  session_note?: string | null
}

/** The place the chat gave (api engine._address): the customer's words + what the calculator read + the shared pin. */
export interface PrefillAddress {
  text: string | null
  /** ตำบล/แขวง (a DHL address the chat gave — "เติมจากแชททั้งหมด"); absent in the summary handoff. */
  tambon?: string | null
  district: string | null
  province: string | null
  postcode: string | null
  pin: { lat: number; lng: number } | null
}

/** The summary handoff's payload — the same cart shape as "สั่งชุดเดิมซ้ำ". */
export type OrderPrefill = RepeatOrderCart

export interface RepeatOrder {
  cart: RepeatOrderCart
}

/**
 * "เติมจากแชททั้งหมด" (owner 18 ต.ค. 69 "สมมุติแชทนั้นคนตอบ 100% ทำไงให้ลงข้อมูลครบเหมือนใช้ AI ตอบ"): what the AI read from a chat
 * the staff answer themselves — the same fields the AI sales brain keeps (api/app/ai/order_read.py). Only what the
 * customer said; unknown = null. The order tab takes it into its EMPTY fields only (stores/script.ts readChat).
 */
export interface OrderRead {
  conversation_id: string
  /** The newest message it read. */
  for_message_id: string
  /** pack, dishes, carrier, the day (deliverable only), the slot key, the kitchen note, the place (`address`). */
  cart: RepeatOrderCart
  /** For "เพิ่มลูกค้าใหม่ใน POS" (a chat with no POS customer): name, the phone the customer typed, the place. */
  customer: { name: string | null; phone: string | null; address: PrefillAddress | null }
  /** Thai labels of what the chat does not say yet ("เบอร์โทร", "รอบเวลา" …) — [] = everything. */
  missing: string[]
  /** What the staff should check ("หาเมนูไม่เจอ: …", "ลูกค้าให้แอดมินจัดเมนูให้ …"). */
  notes: string[]
  /** การสั่งครั้งนี้: the read covered only the chat after this (null / absent = the whole chat). */
  session?: ScriptSession | null
  usage: { model: string | null; tokens_in: number; tokens_out: number; ms: number; cost_usd: number; cost_thb: number | null; cached: boolean }
}

/** WS: the API recomputed this conversation's script. `style_key` (when the script was computed again, not on a step
 *  tick): the customer's style for the inbox row (ConversationSummary.style_key). */
export interface ScriptUpdatedEvent {
  type: 'script.updated'
  conversation_id: string
  style_key?: CustomerStyleKey | null
}

const STYLE_KEYS: readonly string[] = ['polite_short', 'polite_detailed', 'casual', 'curt', 'english']
/** A style key from the API, or null for anything else. */
export const toStyleKey = (v: unknown): CustomerStyleKey | null =>
  typeof v === 'string' && STYLE_KEYS.includes(v) ? (v as CustomerStyleKey) : null

/** The API sends the id as a number (docs/script.md "API contract"); the UI keys every chat by its string id. */
export function toScriptUpdatedEvent(v: unknown): ScriptUpdatedEvent | null {
  if (typeof v !== 'object' || v === null) return null
  const e = v as { type?: unknown; conversation_id?: unknown; style_key?: unknown }
  if (e.type !== 'script.updated') return null
  const id = typeof e.conversation_id === 'number' ? String(e.conversation_id) : e.conversation_id
  if (typeof id !== 'string' || id === '') return null
  return { type: 'script.updated', conversation_id: id, ...('style_key' in e ? { style_key: toStyleKey(e.style_key) } : {}) }
}

export function isScriptUpdatedEvent(v: unknown): v is ScriptUpdatedEvent {
  return toScriptUpdatedEvent(v) !== null
}

export interface ScriptApi {
  /** null = no script for this chat (404). */
  get(convId: string): Promise<ConversationScript | null>
  /** Compute now (the chat was opened while the script was stale, or the staff asked). */
  refresh(convId: string): Promise<ConversationScript | null>
  /** null = nothing to repeat (404). */
  repeatOrder(convId: string): Promise<RepeatOrder | null>
  /** Tick (done) / untick an owner-added step `custom:<n>` → the script with it. Throws ApiError with the reason
   *  (404 no such chat / step — the owner may have removed it · 422 a step the system ticks itself). */
  stepDone(convId: string, key: string, done: boolean): Promise<ConversationScript>
  /** "เติมจากแชททั้งหมด": the AI reads the chat (one model call per chat + newest message; the API keeps the answer).
   *  Throws ApiError with the Thai reason (409 AI mode / switch off · 403 no POS staff link · 422 not a direct chat). */
  orderRead(convId: string, opts?: { auto?: boolean }): Promise<OrderRead>
}

/** Names used by "ปรับตามแนวลูกค้า" (style-adapt) — the same types. */
export type ScriptStyleKey = CustomerStyleKey
export type ScriptMotiveKey =
  | 'delivery_date'
  | 'price'
  | 'weight'
  | 'storage'
  | 'trial'
  | 'fresh'
  | 'taste'
  | 'avoid'
  | 'for_others'
  | 'busy'
  | 'condition'
  | 'rice'
export type ScriptStyle = CustomerStyle
